import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
    DollarSign, 
    ShoppingCart, 
    TrendingUp, 
    AlertTriangle, 
    Truck,
    Package,
    TrendingDown,
    CheckCircle,
    XCircle,
    Receipt
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
    const { isAdmin } = useAuth();
    const [data, setData] = useState(null);
    const [financialData, setFinancialData] = useState(null);
    const [loading, setLoading] = useState(true);

    const TOKEN = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${TOKEN}` } };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/reports/dashboard`);
            setData(response.data);
            
            // Fetch financial data if admin
            if (isAdmin()) {
                try {
                    const financialResponse = await axios.get(`${API_URL}/api/financial/dashboard`, config);
                    setFinancialData(financialResponse.data);
                } catch (err) {
                    console.error('Error fetching financial data:', err);
                }
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const COLORS = ['#D4A84B', '#E5B85C', '#B08D3C', '#F5E6C8', '#8C712F', '#685523', '#443917', '#FDF8ED'];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="w-12 h-12 border-4 border-rr-yellow-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const categoryData = data?.category_sales 
        ? Object.entries(data.category_sales).map(([name, value]) => ({ name, value }))
        : [];

    return (
        <div className="space-y-6 lg:space-y-8 animate-fade-in-up" data-testid="dashboard-page">
            {/* Header */}
            <div>
                <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white">Dashboard</h1>
                <p className="text-gray-400 text-sm lg:text-base mt-1">Visão geral do seu negócio</p>
            </div>

            {/* Stats Cards - PRIMEIRO */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
                <Card className="bg-[#121212] border-[#262626] hover:border-rr-yellow-500/30 transition-colors" data-testid="today-revenue-card">
                    <CardContent className="p-3 lg:p-6">
                        <div className="flex items-start lg:items-center justify-between">
                            <div>
                                <p className="text-[10px] lg:text-xs font-bold uppercase tracking-widest text-gray-500">Vendas Hoje</p>
                                <p className="text-lg lg:text-3xl font-heading font-bold text-rr-yellow-500 mt-1 lg:mt-2">
                                    {formatCurrency(data?.today?.revenue || 0)}
                                </p>
                                <p className="text-xs lg:text-sm text-gray-400 mt-0.5 lg:mt-1">
                                    {data?.today?.count || 0} vendas
                                </p>
                            </div>
                            <div className="w-10 h-10 lg:w-14 lg:h-14 bg-rr-yellow-500/10 rounded-lg flex items-center justify-center">
                                <DollarSign className="w-5 h-5 lg:w-7 lg:h-7 text-rr-yellow-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-[#121212] border-[#262626] hover:border-gray-500/30 transition-colors" data-testid="month-revenue-card">
                    <CardContent className="p-3 lg:p-6">
                        <div className="flex items-start lg:items-center justify-between">
                            <div>
                                <p className="text-[10px] lg:text-xs font-bold uppercase tracking-widest text-gray-500">Vendas Mês</p>
                                <p className="text-lg lg:text-3xl font-heading font-bold text-white mt-1 lg:mt-2">
                                    {formatCurrency(data?.month?.revenue || 0)}
                                </p>
                                <p className="text-xs lg:text-sm text-gray-400 mt-0.5 lg:mt-1">
                                    {data?.month?.count || 0} vendas
                                </p>
                            </div>
                            <div className="w-10 h-10 lg:w-14 lg:h-14 bg-gray-500/10 rounded-lg flex items-center justify-center">
                                <ShoppingCart className="w-5 h-5 lg:w-7 lg:h-7 text-gray-400" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-[#121212] border-[#262626] hover:border-emerald-500/30 transition-colors" data-testid="profit-card">
                    <CardContent className="p-3 lg:p-6">
                        <div className="flex items-start lg:items-center justify-between">
                            <div>
                                <p className="text-[10px] lg:text-xs font-bold uppercase tracking-widest text-gray-500">Lucro Mês</p>
                                <p className={`text-lg lg:text-3xl font-heading font-bold mt-1 lg:mt-2 ${
                                    (data?.month?.profit || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'
                                }`}>
                                    {formatCurrency(data?.month?.profit || 0)}
                                </p>
                                <p className="text-xs lg:text-sm text-gray-400 mt-0.5 lg:mt-1">margem de lucro</p>
                            </div>
                            <div className={`w-10 h-10 lg:w-14 lg:h-14 rounded-lg flex items-center justify-center ${
                                (data?.month?.profit || 0) >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'
                            }`}>
                                <TrendingUp className={`w-5 h-5 lg:w-7 lg:h-7 ${
                                    (data?.month?.profit || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'
                                }`} />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-[#121212] border-[#262626] hover:border-amber-500/30 transition-colors" data-testid="alerts-card">
                    <CardContent className="p-3 lg:p-6">
                        <div className="flex items-start lg:items-center justify-between">
                            <div>
                                <p className="text-[10px] lg:text-xs font-bold uppercase tracking-widest text-gray-500">Alertas</p>
                                <div className="flex items-center gap-3 lg:gap-4 mt-1 lg:mt-2">
                                    <div className="text-center">
                                        <p className="text-lg lg:text-3xl font-heading font-bold text-amber-500">{data?.low_stock || 0}</p>
                                        <p className="text-[10px] lg:text-xs text-gray-500">Estoque</p>
                                    </div>
                                    <div className="w-px h-8 lg:h-10 bg-[#262626]" />
                                    <div className="text-center">
                                        <p className="text-lg lg:text-3xl font-heading font-bold text-blue-500">{data?.pending_deliveries || 0}</p>
                                        <p className="text-[10px] lg:text-xs text-gray-500">Entregas</p>
                                    </div>
                                </div>
                            </div>
                            <div className="w-10 h-10 lg:w-14 lg:h-14 bg-amber-500/10 rounded-lg flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 lg:w-7 lg:h-7 text-amber-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Main Status Indicator - Menor e após os cards */}
            {financialData && isAdmin() && (
                <Card className={`border ${
                    financialData.company_status === 'lucro' 
                        ? 'bg-emerald-500/10 border-emerald-500/50' 
                        : 'bg-red-500/10 border-red-500/50'
                }`}>
                    <CardContent className="p-3 lg:p-4">
                        <div className="flex items-center justify-center gap-4">
                            <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center ${
                                financialData.company_status === 'lucro' 
                                    ? 'bg-emerald-500/20' 
                                    : 'bg-red-500/20'
                            }`}>
                                {financialData.company_status === 'lucro' ? (
                                    <CheckCircle className="w-5 h-5 lg:w-6 lg:h-6 text-emerald-500" />
                                ) : (
                                    <XCircle className="w-5 h-5 lg:w-6 lg:h-6 text-red-500" />
                                )}
                            </div>
                            <div className="text-center">
                                <p className={`text-lg lg:text-xl font-heading font-bold ${
                                    financialData.company_status === 'lucro' ? 'text-emerald-500' : 'text-red-500'
                                }`}>
                                    {financialData.company_status === 'lucro' 
                                        ? `✅ Empresa no LUCRO` 
                                        : `❌ Empresa no PREJUÍZO`}
                                </p>
                                <p className={`text-xl lg:text-2xl font-heading font-bold ${
                                    financialData.company_status === 'lucro' ? 'text-emerald-400' : 'text-red-400'
                                }`}>
                                    {formatCurrency(Math.abs(financialData.summary?.monthly?.net_profit || 0))}
                                </p>
                                <p className="text-[10px] lg:text-xs text-gray-400">Resultado Mensal (Lucro Líquido Real)</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Financial Summary - Complete Breakdown */}
            {financialData && isAdmin() && (
                <Card className="bg-[#121212] border-[#262626]">
                    <CardHeader className="p-4 lg:p-6 pb-2">
                        <CardTitle className="text-white font-heading text-sm lg:text-base flex items-center gap-2">
                            <Receipt className="w-4 h-4 lg:w-5 lg:h-5 text-rr-yellow-500" />
                            Resumo Financeiro Mensal
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 lg:p-6 pt-2">
                        <div className="space-y-3 lg:space-y-4">
                            {/* Receita Total */}
                            <div className="flex items-center justify-between p-3 lg:p-4 bg-[#0a0a0a] rounded-lg border border-[#1a1a1a]">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                                        <DollarSign className="w-5 h-5 lg:w-6 lg:h-6 text-blue-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs lg:text-sm text-gray-400">Receita Total (Vendas)</p>
                                        <p className="text-lg lg:text-xl font-bold text-blue-500">
                                            {formatCurrency(financialData.summary?.monthly?.revenue || 0)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* CPV */}
                            <div className="flex items-center justify-between p-3 lg:p-4 bg-[#0a0a0a] rounded-lg border border-[#1a1a1a]">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 lg:w-12 lg:h-12 bg-orange-500/10 rounded-lg flex items-center justify-center">
                                        <Package className="w-5 h-5 lg:w-6 lg:h-6 text-orange-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs lg:text-sm text-gray-400">CPV (Custo dos Produtos Vendidos)</p>
                                        <p className="text-lg lg:text-xl font-bold text-orange-500">
                                            - {formatCurrency(financialData.summary?.monthly?.cpv || 0)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Divider with Calculation */}
                            <div className="flex items-center gap-2 px-2">
                                <div className="flex-1 h-px bg-[#262626]"></div>
                                <span className="text-xs text-gray-500">Receita - CPV =</span>
                                <div className="flex-1 h-px bg-[#262626]"></div>
                            </div>

                            {/* Lucro Bruto */}
                            <div className="flex items-center justify-between p-3 lg:p-4 bg-[#0a0a0a] rounded-lg border border-emerald-500/30">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 lg:w-12 lg:h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                                        <TrendingUp className="w-5 h-5 lg:w-6 lg:h-6 text-emerald-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs lg:text-sm text-gray-400">Lucro Bruto</p>
                                        <p className="text-lg lg:text-xl font-bold text-emerald-500">
                                            {formatCurrency(financialData.summary?.monthly?.profit || 0)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Total de Despesas */}
                            <div className="flex items-center justify-between p-3 lg:p-4 bg-[#0a0a0a] rounded-lg border border-[#1a1a1a]">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 lg:w-12 lg:h-12 bg-red-500/10 rounded-lg flex items-center justify-center">
                                        <TrendingDown className="w-5 h-5 lg:w-6 lg:h-6 text-red-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs lg:text-sm text-gray-400">Total de Despesas do Mês</p>
                                        <p className="text-lg lg:text-xl font-bold text-red-500">
                                            - {formatCurrency(financialData.summary?.monthly?.expenses || 0)}
                                        </p>
                                        <div className="flex gap-3 mt-1">
                                            <span className="text-xs text-emerald-400">
                                                ✓ Pagas: {formatCurrency(financialData.summary?.monthly?.expenses_paid || 0)}
                                            </span>
                                            <span className="text-xs text-amber-400">
                                                ⏳ Pendentes: {formatCurrency(financialData.summary?.monthly?.expenses_pending || 0)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Divider with Calculation */}
                            <div className="flex items-center gap-2 px-2">
                                <div className="flex-1 h-px bg-[#262626]"></div>
                                <span className="text-xs text-gray-500">Lucro Bruto - Despesas =</span>
                                <div className="flex-1 h-px bg-[#262626]"></div>
                            </div>

                            {/* Lucro Líquido Real */}
                            <div className={`flex items-center justify-between p-4 lg:p-5 rounded-lg border-2 ${
                                financialData.company_status === 'lucro' 
                                    ? 'bg-emerald-500/10 border-emerald-500/50' 
                                    : 'bg-red-500/10 border-red-500/50'
                            }`}>
                                <div className="flex items-center gap-3">
                                    <div className={`w-12 h-12 lg:w-14 lg:h-14 rounded-lg flex items-center justify-center ${
                                        financialData.company_status === 'lucro' 
                                            ? 'bg-emerald-500/20' 
                                            : 'bg-red-500/20'
                                    }`}>
                                        {financialData.company_status === 'lucro' ? (
                                            <CheckCircle className="w-6 h-6 lg:w-7 lg:h-7 text-emerald-500" />
                                        ) : (
                                            <XCircle className="w-6 h-6 lg:w-7 lg:h-7 text-red-500" />
                                        )}
                                    </div>
                                    <div>
                                        <p className="text-xs lg:text-sm text-gray-400">Lucro Líquido Real</p>
                                        <p className={`text-xl lg:text-2xl font-bold ${
                                            financialData.company_status === 'lucro' ? 'text-emerald-500' : 'text-red-500'
                                        }`}>
                                            {formatCurrency(financialData.summary?.monthly?.net_profit || 0)}
                                        </p>
                                    </div>
                                </div>
                                <div className={`px-3 py-1.5 rounded-full text-xs lg:text-sm font-bold ${
                                    financialData.company_status === 'lucro' 
                                        ? 'bg-emerald-500/20 text-emerald-400' 
                                        : 'bg-red-500/20 text-red-400'
                                }`}>
                                    {financialData.company_status === 'lucro' ? 'LUCRO' : 'PREJUÍZO'}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
                {/* Daily Sales Chart */}
                <Card className="lg:col-span-2 bg-[#121212] border-[#262626]" data-testid="daily-sales-chart">
                    <CardHeader className="p-4 lg:p-6">
                        <CardTitle className="text-white font-heading text-sm lg:text-base">Vendas dos Últimos 7 Dias</CardTitle>
                    </CardHeader>
                    <CardContent className="p-2 lg:p-6 pt-0">
                        <div className="h-48 sm:h-64 lg:h-80 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data?.daily_sales || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                                    <XAxis dataKey="date" stroke="#888" tick={{ fontSize: 10 }} />
                                    <YAxis stroke="#888" tickFormatter={(v) => `R$${v}`} tick={{ fontSize: 10 }} width={50} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#121212',
                                            border: '1px solid #262626',
                                            borderRadius: '6px',
                                            fontSize: '12px'
                                        }}
                                        formatter={(value) => [formatCurrency(value), 'Receita']}
                                    />
                                    <Bar dataKey="revenue" fill="#D4A84B" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Category Sales Pie Chart */}
                <Card className="bg-[#121212] border-[#262626]" data-testid="category-chart">
                    <CardHeader className="p-4 lg:p-6">
                        <CardTitle className="text-white font-heading text-sm lg:text-base">Vendas por Categoria</CardTitle>
                    </CardHeader>
                    <CardContent className="p-2 lg:p-6 pt-0">
                        <div className="h-48 sm:h-64 lg:h-80 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={categoryData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={40}
                                        outerRadius={70}
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {categoryData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#121212',
                                            border: '1px solid #262626',
                                            borderRadius: '6px',
                                            fontSize: '12px'
                                        }}
                                        formatter={(value) => formatCurrency(value)}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        {/* Legend */}
                        <div className="mt-2 lg:mt-4 grid grid-cols-2 gap-1.5 lg:gap-2">
                            {categoryData.slice(0, 6).map((item, index) => (
                                <div key={item.name} className="flex items-center gap-1.5 lg:gap-2">
                                    <div 
                                        className="w-2 h-2 lg:w-3 lg:h-3 rounded-sm flex-shrink-0" 
                                        style={{ backgroundColor: COLORS[index % COLORS.length] }} 
                                    />
                                    <span className="text-[10px] lg:text-xs text-gray-400 truncate">{item.name}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Top Products */}
            <Card className="bg-[#121212] border-[#262626]" data-testid="top-products">
                <CardHeader className="p-4 lg:p-6">
                    <CardTitle className="text-white font-heading text-sm lg:text-base flex items-center gap-2">
                        <Package className="w-4 h-4 lg:w-5 lg:h-5 text-rr-yellow-500" />
                        Produtos Mais Vendidos (Mês)
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-4 lg:p-6 pt-0">
                    <div className="space-y-2 lg:space-y-4">
                        {(data?.top_products || []).map((product, index) => (
                            <div 
                                key={index}
                                className="flex items-center justify-between p-2 lg:p-4 bg-[#0a0a0a] rounded-md border border-[#1a1a1a] hover:border-rr-yellow-500/30 transition-colors"
                            >
                                <div className="flex items-center gap-2 lg:gap-4 min-w-0">
                                    <div className="w-8 h-8 lg:w-10 lg:h-10 bg-rr-yellow-500/10 rounded-md flex items-center justify-center text-rr-yellow-500 font-bold text-sm lg:text-base flex-shrink-0">
                                        {index + 1}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-white font-medium text-sm lg:text-base truncate">{product.name}</p>
                                        <p className="text-xs lg:text-sm text-gray-500">{product.quantity} un.</p>
                                    </div>
                                </div>
                                <p className="text-rr-yellow-500 font-bold text-sm lg:text-base flex-shrink-0 ml-2">{formatCurrency(product.revenue)}</p>
                            </div>
                        ))}
                        {(!data?.top_products || data.top_products.length === 0) && (
                            <p className="text-gray-500 text-center py-6 lg:py-8 text-sm">Nenhuma venda registrada este mês</p>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default DashboardPage;
